import { CircularProgress } from "@mui/material";
import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { ElogMeetingContext } from "../context";
import ListView from "@common/components/ListDetailViews/ListView";
import { getColumns } from "./colDef";
import { WithGrow } from "@common/components/WithGrow";
import FilteringBar from "@common/components/FilteringBar";
import { GridContainer } from "@common/components/ListDetailViews/DetailView/DeviceCard/DeviceCardAccordion";
import { ElogParticipant } from "@services/meetings/types";
import DeleteButton from "@common/components/Button/DeleteButton";
import Drawer from "@pages/Meeting/DetailView/ItemPanel/Accordions/Elog/Drawer";
import { useErrorNotification } from "@common/components/Notification";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { FiltersTypeImpl } from "@common/utils/filters";
import { canEditElogsAndRevisions } from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";

const Elogs = () => {
  const { t } = useTranslation();
  const dispatchError = useErrorNotification();

  const { form, dispatchElogMeeting } = useContext(ElogMeetingContext);
  const context = useContext(RoomsRealtimeContext);
  const data = form.elogMeeting.elogParticipants ?? [];
  const [selected, setSelected] = useState<ElogParticipant["id"][]>([]);
  const [user] = useAuthenticatedUser();
  const count = selected.length;
  const [open, setOpen] = useState<false | Partial<ElogParticipant>>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const handleOpenElog = (id: number) => {
    setOpen(
      form.elogMeeting?.elogParticipants?.find((e) => e.id == id) ?? false
    );
  };
  const columns = getColumns((id: number) => handleOpenElog(id), t);
  const handleDelete = useCallback(async () => {
    try {
      if (form.elogMeeting === undefined) return;
      setLoading(true);
      setSelected([]);
      dispatchElogMeeting({
        ...form.elogMeeting,
        elogParticipants: (form.elogMeeting?.elogParticipants ?? []).filter(
          ({ id }) => !selected.includes(id)
        ),
      });
    } catch (e) {
      dispatchError(e);
    } finally {
      setLoading(false);
    }
  }, [selected, form.elogMeeting, context.detail?.meeting?.id]);

  const handleSave = useCallback(
    async (participant: Partial<ElogParticipant>) => {
      if (form.elogMeeting === undefined) return;
      setLoading(true);
      try {
        let elogParticipants = form.elogMeeting.elogParticipants ?? [];
        if (participant.id)
          elogParticipants = elogParticipants.map((p) =>
            p.id === participant.id ? (participant as ElogParticipant) : p
          );
        else elogParticipants.push(participant as ElogParticipant);
        dispatchElogMeeting({
          ...form.elogMeeting,
          elogParticipants,
        });
        setOpen(false);
      } catch (e) {
        dispatchError(e);
      } finally {
        setLoading(false);
      }
    },
    [form.elogMeeting, context.detail?.meeting?.id]
  );

  const canEdit = canEditElogsAndRevisions(user);
  return (
    <WithGrow
      direction="column"
      fixed={
        <>
          <GridContainer
            container
            direction="row"
            alignItems="center"
            wrap="nowrap"
            sx={{ width: '100%', backgroundColor: "transparent" }}
          >
            <FilteringBar
              disableAddButton={!canEdit}
              type={FiltersTypeImpl.Accounts}
              refetch={() => {
                setLoading(true);
                context.detail?.refetch();
                setLoading(false);
              }}
              filters={[]}
              setFilters={() => {}}
              hideFilters={true}
              onAdd={() => setOpen({})}
              deleteButton={
                <DeleteButton
                  count={count}
                  label={t(`Delete {{count}} E-Log${count === 1 ? "" : "s"}`, {
                    count,
                  })}
                  onClick={() => handleDelete()}
                  withConfirm
                  ConfirmDialogProps={{
                    actionLabel: t(`Delete E-Log${count === 1 ? "" : "s"}`),
                    confirmPrompt: t(
                      count === 1
                        ? "Are you sure you want to delete this E-Log?"
                        : "Are you sure you want to delete {{count}} E-Logs?",
                      { count }
                    ),
                  }}
                />
              }
            />
            <Drawer
              open={Boolean(open)}
              onClose={() => setOpen(false)}
              onSave={handleSave}
              participant={open ? open : undefined}
            />
          </GridContainer>
        </>
      }
    >
      {form.elogMeeting === undefined ? (
        <CircularProgress sx={{ position: "relative", left: "50%" }} />
      ) : (
        <ListView
          loading={loading}
          data={
            data.sort((a, b) =>
              (a.room?.name || "").localeCompare(b.room?.name || "")
            ) ?? []
          }
          columns={columns}
          total={data?.length}
          selectable={true}
          identityKey="id"
          onSelect={setSelected}
        />
      )}
    </WithGrow>
  );
};

export default Elogs;
